import { useEffect, useState } from "react";

/** Parse the URL fragments into an object
 *
 * @param {string} fragmenturl - The URL fragment to parse
 * @returns {Object} - The parsed fragments
 * @example
 * ```js
 * urlToFragment("#key=value#key=value")
 * // Returns { key: value, key: value }
 * ```
 */
const urlToFragment = (fragmenturl) => {
  const fragments = fragmenturl.split("#");
  return fragments.reduce((acc, fragment) => {
    const [key, value] = fragment.split("=");
    if (key && value) acc[key] = value;
    return acc;
  }, {});
};

/** Convert an object into URL fragments
 *
 * @param {Object} fragments - The fragments to convert
 * @returns {string} - The URL fragment
 * @example
 * ```js
 * fragmentsToUrl({ key: value, key: value })
 * // Returns "#key=value#key=value"
 * ```
 */
const fragmentsToUrl = (fragments) => {
  return Object.entries(fragments).reduce((acc, [key, value]) => {
    let parsedValue = value;
    if (Array.isArray(parsedValue)) parsedValue = parsedValue.join(",");
    if (!parsedValue) return acc;
    return acc + `#${key}=${parsedValue}`;
  }, "");
};

/** Hook to manage URL fragments.
 *
 * @returns {Object} - The URL fragments and functions to manage them
 * @example
 * ```js
 * const { urlFragments, addFragments, removeFragments, clearFragments } = useFragments();
 * ```
 */
const useFragments = () => {
  if (!window || typeof window === "undefined") {
    throw new Error("useFragments can only be used in a client context.");
  }

  const [urlFragments, setUrlFragments] = useState({});

  const retrieveFragments = () => {
    setUrlFragments(urlToFragment(window.location.hash));
  };

  useEffect(() => {
    retrieveFragments();

    window.addEventListener("hashchange", retrieveFragments);

    return () => {
      window.removeEventListener("hashchange", retrieveFragments);
    };
  }, []);

  /** Add keyed fragments to the URL
   *
   * @param {Object} fragments - An array of fragments or a single fragment.
   * @example
   * ```js
   * addFragments({ key: value, key: value })
   * addFragments({ key: value })
   * ```
   */
  const addFragments = (fragments, replaceAll = false) => {
    console.log("addFragment", {
      fragments,
      oldHash: window.location.hash,
      newHash: fragmentsToUrl({ ...(replaceAll ? {} : urlFragments), ...fragments }),
    })
    window.location.hash = fragmentsToUrl({
      ...(replaceAll ? {} : urlFragments),
      ...fragments,
    });
  };

  /** Remove fragments from the URL
   *
   * @param {Array} keys - An array of keys to remove from the URL
   * @example
   * ```js
   *  removeFragments(["key", "key"])
   * ```
   */
  const removeFragments = (keys) => {
    if (!Array.isArray(keys)) return;
    const updatedFragments = { ...urlFragments };
    keys.forEach((key) => delete updatedFragments[key]);
    addFragments(updatedFragments, true);
  };

  /** Clear all fragments from the URL */
  const clearFragments = () => addFragments({}, true);

  return { urlFragments, addFragments, removeFragments, clearFragments };
};

export default useFragments;
